import React, { Fragment, useEffect } from "react";
import { NavLink } from "react-router-dom";
import clsx from "clsx";

// MUI
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import List from "@material-ui/core/List";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ToolTip from "@material-ui/core/Tooltip";
import { Avatar, useMediaQuery } from "@material-ui/core";

// Icons
import MenuIcon from "@material-ui/icons/MenuRounded";
import MenuOpenIcon from "@material-ui/icons/MenuOpenRounded";
import ExitToAppIcon from "@material-ui/icons/ExitToAppRounded";
import ChevronRightIcon from "@material-ui/icons/ChevronRightRounded";
import CategoryIcon from "@material-ui/icons/CategoryRounded";
import AdIcon from "@material-ui/icons/LoupeRounded";
import DashboardIcon from "@material-ui/icons/DashboardRounded";
import AppIcon from "@material-ui/icons/DonutSmallRounded";
import SmartAppIcon from "@material-ui/icons/SmartphoneRounded";
import ClassIcon from "@material-ui/icons/ClassRounded";
import ProductIcon from "@material-ui/icons/Loyalty";

// Logo
import Logo from "../../images/logo.png";
import NavItem from "./NavItem";
import { useStateValue } from "../../store/provider";
import { LOGOUT } from "../../store/reducer";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9) + 1,
    },
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.common.white,
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  title: {
    flex: 1,
    textAlign: "right",
  },
  navLink: {
    "&.active": {
      background: theme.palette.primary[50],
      borderLeft: `4px solid ${theme.palette.primary.light}`,
    },
  },
}));

const NavBar = () => {
  const classes = useStyles();
  const theme = useTheme();

  const mediaQuery = useMediaQuery(theme.breakpoints.down("sm"));

  const [{ isAuth, admin }, dispatch] = useStateValue();

  const [open, setOpen] = React.useState(true);
  const [title, setTitle] = React.useState("Dashboard");

  useEffect(() => {
    setOpen(!mediaQuery);
  }, [mediaQuery]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleLogout = () => {
    dispatch({ type: LOGOUT });
  };

  return !isAuth ? (
    <Fragment></Fragment>
  ) : (
    <Fragment>
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open,
        })}
      >
        <Toolbar>
          {!open && (
            <img
              src={Logo}
              alt="App Browser"
              style={{ marginRight: 20, maxWidth: 50 }}
            />
          )}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            className={clsx(classes.menuButton, {
              [classes.hide]: open,
            })}
          >
            <MenuIcon />
          </IconButton>
          {!open ? (
            <Typography variant="h6">App Browser</Typography>
          ) : (
            <Typography style={{ marginLeft: 20 }} variant="h6">
              {title}
            </Typography>
          )}

          <div className={classes.title}>
            <IconButton component={NavLink} exact to={`/profile`}>
              <Avatar src={admin.image} />
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
      >
        <div className={classes.toolbar}>
          <img src={Logo} alt="App Browser" style={{ width: 50 }} />
          <Typography variant="h6">App Browser</Typography>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <MenuOpenIcon style={{ color: "white" }} />
            )}
          </IconButton>
        </div>

        <List>
          <NavItem
            className={classes.navLink}
            path="/dashboard"
            icon={DashboardIcon}
            title="Dashboard"
            func={setTitle}
          />
          <NavItem
            className={classes.navLink}
            path="/category"
            icon={CategoryIcon}
            title="Category"
            func={setTitle}
          />
          <NavItem
            className={classes.navLink}
            path="/app"
            icon={AppIcon}
            title="App"
            func={setTitle}
          />
          <NavItem
            className={classes.navLink}
            path="/smartapp"
            icon={SmartAppIcon}
            title="Smart App"
            func={setTitle}
          />
          <NavItem
            className={classes.navLink}
            path="/productCategory"
            icon={ClassIcon}
            title="Product Category"
            func={setTitle}
          />
          <NavItem
            className={classes.navLink}
            path="/product"
            icon={ProductIcon}
            title="Product"
            func={setTitle}
          />
          <NavItem
            className={classes.navLink}
            path="/advertisement"
            icon={AdIcon}
            title="Advertisement"
            func={setTitle}
          />
        </List>

        <Divider />

        <List>
          <ListItem button className={classes.navLink} onClick={handleLogout}>
            <ToolTip title="Log out">
              <ListItemIcon>
                <ExitToAppIcon color="primary" />
              </ListItemIcon>
            </ToolTip>
            <ListItemText primary="Log out" />
          </ListItem>
        </List>
      </Drawer>
    </Fragment>
  );
};

export default React.memo(NavBar);
